import * as React from 'react';

import { getEntityFromProfile } from 'utils/view';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import MetricsChannelsBasicChannelsHOC from 'components/metrics/channels-basic/MetricsChannelsBasicChannelsHOC';
import CreatorTileArtistName from 'components/creator-tile/artist-name/CreatorTileArtistName';
import CreatorTileLocations from 'components/creator-tile/locations/CreatorTileLocations';
import CreatorTilePriceReco from 'components/creator-tile/price-reco/CreatorTilePriceReco';
import CreatorTileImageGrid from 'components/creator-tile/grid/CreatorTileImageGrid';

import { getInfluencerFullName } from 'utils/influencers';
import { getImagesFromChannel } from 'utils/influencer-tile';

import type { ImageGridImage, InfluencerTile } from 'types';

type Props = {
  influencer: InfluencerTile,
  shouldLoadImages: boolean
};

const RelatedInfluencerCard = ({
  influencer,
  shouldLoadImages
}: Props): JSX.Element => {
  const [images, setImages] = React.useState([] as Array<ImageGridImage>);

  React.useEffect(() => {
    if (shouldLoadImages) {
      setImages(getImagesFromChannel(influencer, 'instagram'));
    }
  }, [shouldLoadImages]);

  const entity = getEntityFromProfile(influencer);
  const fullName = getInfluencerFullName(influencer);

  return (
    <div className="related-influencer-card">
      <EntityAvatar
        bordered
        entity={entity}
        link={entity?.url}
        customSize={{
          width: 64,
          height: 64
        }}
        openInNewTab
      />
      <div className="related-influencer-card__artist-name">
        <CreatorTileArtistName
          influencer={influencer}
          url={entity?.url ?? ''}
        />
      </div>
      {
        fullName && (
          <a
            href={entity?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="related-influencer-card__full-name text-overflow-ellipsis"
            title={fullName}
          >
            {`(${fullName})`}
          </a>
        )
      }
      <div className="related-influencer-card__extra-information">
        <CreatorTileLocations influencer={influencer} />
        <CreatorTilePriceReco
          size="large"
          className="related-influencer-card__price-reco"
          containerClassName="related-influencer-card__price-reco-ranges"
          channelsData={influencer.channels}
          channels={['instagram', 'tiktok']}
          caption={(
            <SVGDynamicIcon
              name="euro-inside-circle-custom-fg"
              width={16}
              height={16}
              customColor="grey"
            />
          )}
          captionClassName="related-influencer-card__price-reco-icon"
        />
      </div>
      <div className="related-influencer-card__channels">
        <MetricsChannelsBasicChannelsHOC
          channels={influencer.channels}
          showChannels={['instagram', 'tiktok', 'youtube']}
          type="limited"
          includeRatio
        />
      </div>
      <div className="related-influencer-card__image-grid-placeholder" />
      <div className="related-influencer-card__image-grid">
        <CreatorTileImageGrid
          images={images}
          columns={2}
          rows={2}
          count={4}
          gutter={8}
        />
      </div>
    </div>
  );
};

export default RelatedInfluencerCard;
